import React from "react";
import {History} from "react-router";
import PageAncestor from "core/common/page/pageAncestor";
import Toolmenu from "core/components/toolmenu/toolmenu";
import mdEntityService from "core/metamodel/mdEntityService";
import securityService from "core/security/securityService";
import {BlockComp, MdDivider} from "core/components/blockComp/blockComp";
import {ClosedContentProps, FieldText, FormProps, IDomainObject, lovLabel, preSave} from "core/form/formUtils";
import {ErrorComp} from "core/components/errorComp/errorComp";
import ConfirmationDialog from "core/components/confirmationDialog/confirmationDialog";
import {MdTextField} from 'core/components/fields/fields';
import {createForm, fieldStyles} from 'core/form/createForm';
import {PrimaryKeyString} from 'core/form/rules';
import ActiveItem from 'core/components/blockComp/activeItem';
import {Validate} from 'core/form/validate';
import RaisedButton from 'material-ui/RaisedButton';
import HTML5Backend from 'react-dnd-html5-backend';
import {DragDropContext} from 'react-dnd';
import {SecurablesAssigned} from "core/security/component/securableAssigned";
import {MODULE, PageProps, PERMISSION} from "core/metamodel/metaConstant";
import {SecurableDefinitionModuleMap, SecurityRole} from "core/security/securityDomain";
import {action} from "mobx";
import uiService from "core/common/service/uiService";
import {WithDeleteDialogZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import {observer} from "mobx-react";

import "core/security/component/securableBlocks.less";
import {removeItemFromLocalStorage} from "core/common/utils/storage";
import {FIELDDEFDISPLAYTYPE} from "core/form/form";
import {loc} from "core/common/config/locales";
import StyledSelect from "core/components/fields/styledSelect";
import Chip from "material-ui/Chip";
import {red300} from "material-ui/styles/colors";
import {SecurableDrawer} from "core/security/component/SecurableDrawer";

const KNOWN_STATICS = ["fetchData", "handler", "willTransitionTo", "willTransitionFrom", "module", "title", "icon", "protectedPage", "rootPage"];

function recoverLostStatics(componentWrappedByDnd) {
  for(let key of KNOWN_STATICS) {
    componentWrappedByDnd[key] = componentWrappedByDnd.DecoratedComponent[key];
  }
}


interface SecurityRoleDetailState {
  openDrawer?: boolean;
  loadingDrawer?: boolean;
}

@recoverLostStatics
@DragDropContext(HTML5Backend)
@observer
export default class SecurityRoleDetail extends PageAncestor<PageProps, SecurityRoleDetailState>  {

  static module = MODULE.core;
  static title = 'Security Role';
  static icon = 'user-secret';
  static protectedPage = true;



  static fetchData(routerParams, query) {
    console.log("SecurityRoleDetail#fetchData()", this, SecurityRoleDetail);

    let metadataPromise = mdEntityService.fetchEntityMetadata(['SecurityRole'], [{
      entity: 'MODULE',
      lov: true
    }, {entity: 'SECURABLETYPE', lov: true}, {entity: 'SECURABLE', lov: true},  {entity: 'RESTRICTION', lov: true}]);

    let securablesPromise = securityService.getSecurables();


    let securityRolePromise;
    if (routerParams.id === 'new') {
      securityRolePromise = Promise.resolve(new SecurityRole(Object.assign({
        permissionMap: {},
        users: [],
        restrictions: [],
        $new: true
      }, query)));
    } else if (routerParams.id === 'copy') {
      securityRolePromise = securityService.readSecurityRole(query.copyId)
        .then(securityRoleObject => {
          securityRoleObject.cleanOnCopy();
          securityRoleObject.users = [];
          return securityRoleObject;
        });
    } else {
      securityRolePromise = securityService.readSecurityRole(routerParams.id);
    }


    return Promise.all([metadataPromise, securityRolePromise, securablesPromise])
      .then(([entityMap, securityRoleObject, securables]: [any, SecurityRole, SecurableDefinitionModuleMap]) => {
        securityService.securityRoleObject = securityRoleObject.initAfterRead(routerParams.id === 'new');
        securityService.securables = securables;
      })
  }




state = {
    openDrawer: false,
    loadingDrawer: false
  };


  componentWillUnmount() {
    securityService.securityRoleObject = null;
  }


  customValidate = (securityRoleObject: SecurityRole) => {
    let errors = [];
    if (securityRoleObject.permissions.length === 0 && ( !securityRoleObject.restrictions || !securityRoleObject.restrictions.length) ) {
      errors.push({message: "At least one securable or restriction must be selected"})
    }
    return errors;
  };

  findRootObject() {
    return securityService.securityRoleObject;
  }


  @action onSave = (evt) => {
    console.log('onSave');
    uiService.showToolbarActionFlag();

    securityService.securityRoleObject.permissions = securityService.securityRoleObject.permissions.filter(sp => sp.getPermissionList().length > 0 || sp.detailPermissions.size > 0 );

    let result = preSave(securityService.securityRoleObject, this.customValidate);
    if (!result) {
      return;
    }

    let promise = securityService.saveSecurityRole(securityService.securityRoleObject);
    promise.then(action("saveSecurityRole-actopm", (secRoleJson: IDomainObject) => {

      let wasNew = securityService.securityRoleObject.cleanAfterSave(secRoleJson);
      if(wasNew) {
        this.context.router.replaceWith('securityRoleDetail', {id: securityService.securityRoleObject.roleId});
      }
      removeItemFromLocalStorage("MdEntity", loc.lang, "SecurityRole");
      uiService.hideToolbarActionFlag();
    }));

  };


  @action onDelete = (evt) => {
    console.log('onDelete');
    securityService.deleteSecurityRole(securityService.securityRoleObject.roleId)
      .then(response => {
        removeItemFromLocalStorage("MdEntity", loc.lang, "SecurityRole");
        this.goBackOrHome();
      }, error => {
        securityService.securityRoleObject.$errors = error.getMessages();
      });
  };


  onAdd = (evt) => {
    console.log('onAdd');
    this.context.router.transitionTo("securityRoleDetail", {id: 'new'});
  };

  onCopy = (evt) => {
    console.log('onCopy');
    this.context.router.transitionTo("securityRoleDetail", {id: 'copy'}, {copyId: securityService.securityRoleObject.roleId});
  };


  openDrawer = () => {
    this.setState({openDrawer: !this.state.openDrawer});
  };


  handleDrawerRequestChange = (evt) => {
    this.setState({openDrawer: false});
  };

  truish = () => true;

  @action handleRestriction = (valueArray) => {
    console.log('handleRestriction %O', valueArray);
    securityService.securityRoleObject.restrictions = valueArray;
    securityService.securityRoleObject.$dirty = true;
  };


  render() {

    let {
      query
    } = this.props;

    let propsForCreateForm = {
      dataObject: securityService.securityRoleObject,
      rootObject: securityService.securityRoleObject,
      entity: mdEntityService.getEntity('SecurityRole'),
      entities: mdEntityService.entityMap
    };

    console.debug('%c securityRoleDetail render', 'background-color: yellow');


    return (
      <main className="main-content" style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {this._createToolMenu(securityService.securityRoleObject, query.gridLocation)}

        <ErrorComp messages={securityService.securityRoleObject.$errors}/>
        <ConfirmationDialog ref={this.dirtyDialogRefHandler}/>
        <ConfirmationDialog ref={this.deleteDialogRefHandler} okButtonText={loc.i18n`Yes`} cancelButtonText={loc.i18n`No`}/>

        { this.state.openDrawer &&
        <SecurableDrawer entities={mdEntityService.entityMap} open={this.state.openDrawer}
                         securables={securityService.securables} securityRoleObject={securityService.securityRoleObject} onRequestChange={this.handleDrawerRequestChange}/> }


        <div style={{marginTop: 20, display: "flex"}}>
          <BlockComp noPadding className="block-comp-right-border" style={{flexGrow: 1, minWidth: "30%"}} header="Security Role">
            <form >
              <SecurityRoleForm {...propsForCreateForm} />
            </form>
          </BlockComp>

          <BlockComp noPadding header="Restrictions" style={{flexGrow: 1}}>
            <ActiveItem openContent={
              <div className="row">
                <div className="col-xs-12">
                  <StyledSelect {...fieldStyles.small.styledSelect}
                                onChange={this.handleRestriction} value={securityService.securityRoleObject.restrictions.slice()}
                                options={mdEntityService.getEntity('RESTRICTION').lovItems}
                                hintText="Choose values" multi fullWidth searchable={false} clearable={false}/>
                </div>
              </div>
            } closedContent={
              <div
                style={{fontSize: 14, lineHeight: '14px', minHeight: 20, display: 'flex', flexWrap: 'wrap', margin: -4}}>
                {securityService.securityRoleObject.restrictions.map( (rst, index) => <Chip key={index}
                                                                                            style={{margin: 3, backgroundColor: red300}}>{lovLabel(rst)}</Chip>)}
              </div>
            } validate={this.truish} dataObject={securityService.securityRoleObject} entities={mdEntityService.entityMap} formName="securityRoleRestrictionsForm"
                        rootObject={securityService.securityRoleObject} />
          </BlockComp>
        </div>


        <div style={{marginLeft: 20, marginTop: 10}}>
          <RaisedButton primary onClick={this.openDrawer} label="Add Securable"/>
        </div>

        <SecurablesAssigned entities={mdEntityService.entityMap} securityRoleObject={securityService.securityRoleObject} securables={securityService.securables}/>

        <MdDivider inset marginTop={20}/>
        <BlockComp header={<span>Users</span>}>
          <table className="zz-simple-table">
            <thead>
            <tr>
              <th>Username</th>
              <th>Full name</th>
            </tr>
            </thead>
            <tbody>
            {securityService.securityRoleObject.users.map((userLov, index) => (
              <tr key={index}>
                <td>{userLov.value}</td>
                <td>{userLov.label}</td>
              </tr>
            ))}

            </tbody>
          </table>
        </BlockComp>


      </main>
    );

  }


  _createToolMenu(securityRoleObject: SecurityRole, gridLocation) {
    return (
      <Toolmenu chainGridLocation={gridLocation} backAction={this.onBack} backDisabled={History.length <= 1} historize entityName="SecurityRole" entityId={securityRoleObject.roleId}>
        <ZzFlatButton onClick={this.onSave} disabled={!securityRoleObject.$dirty} label="Save" permission={securityRoleObject.$new ? PERMISSION.New : PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onAdd} disabled={securityRoleObject.$new} label="New" permission={PERMISSION.New}/>
        <ZzFlatButton onClick={this.onCopy} disabled={securityRoleObject.$new} label="Copy" permission={PERMISSION.New}/>
        <WithDeleteDialogZzFlatButton entityName="SecurityRole" pageAncestor={this} onClick={this.onDelete} disabled={securityRoleObject.$new} label="Delete" permission={PERMISSION.Delete}/>
      </Toolmenu>
    );
  }


}

const ClosedContent = ({dataObject, entity, entities, fields, disabled}: ClosedContentProps<SecurityRole>) => (
  <div style={{display: 'flex', flexDirection: 'column'}}>
    <h5 style={{marginTop: 0, fontSize: '18px', fontWeight: 'bold'}}> {dataObject.roleName}</h5>
    <div className="form-text-row">
      <FieldText label={fields.roleId.textLabel} value={dataObject.roleId}/>
    </div>
    <div className="form-text-row">
      <FieldText label={fields.comment.textLabel} value={dataObject.comment}/>
    </div>
  </div>
);

@createForm({
  formName: 'SecurityRoleForm',
  fields: [{
    name: 'roleId',
    validators: ['IsRequired']
  }, {
    name: 'roleName',
    validators: ['IsRequired']
  }, {
    name: 'comment',
    displayType: FIELDDEFDISPLAYTYPE.textArea
  }]
}, ClosedContent)
@observer
class SecurityRoleForm extends React.Component<FormProps<SecurityRole, SecurityRole>, undefined> {

  render() {


    let {dataObject, rootObject, entities, formName, entity} = this.props;
    let fields = dataObject.getFormFields(formName);
    let {roleId, roleName, comment} = fields;

    let openContent = (
      <div>

        <div className="row">
          <div className="fx col-xs-12 col-sm-4">
            <MdTextField {...roleId.props} disabled={!dataObject.$new}/>
            <Validate field={roleId} value={roleId.props.value}>
              <PrimaryKeyString/>
            </Validate>
          </div>
          <div className="fx col-xs-12 col-sm-6">
            <MdTextField {...roleName.props} />
            <Validate field={roleName} value={roleName.props.value}/>
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-12">
            <MdTextField {...comment.props} multiLine={true} rows={2}/>
          </div>
        </div>

      </div>
    );

    return (
      <BlockComp noPadding>
        <ActiveItem openContent={openContent} closedContent={<ClosedContent {...{dataObject, entity, entities, fields}} />}
                    validate={this.props.validate} {...this.props} />
      </BlockComp>
    );
  }
}





// WEBPACK FOOTER //
// ./src/core/security/securityRoleDetail.tsx